package com.trevorwiebe.timesheet.admin.usermanagement.presentation

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuAnchorType
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.core.domain.model.OwnerOperator
import com.trevorwiebe.timesheet.core.domain.model.UserRole
import com.trevorwiebe.timesheet.core.presentation.common.DialogButtonRow
import com.trevorwiebe.timesheet.core.presentation.common.NativeDatePicker
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.TimesheetTextField
import com.trevorwiebe.timesheet.core.presentation.common.responsiveDialogWidth

fun UserRole.label(): String = name.lowercase().replaceFirstChar { it.uppercase() }

@Composable
expect fun AddEditUserDialog(
    editing: DatabaseUserModel?,
    isSaving: Boolean,
    isSendingPasswordReset: Boolean,
    passwordResetEmailSent: Boolean,
    currentUserId: String?,
    ownerOperators: List<OwnerOperator>,
    onDismiss: () -> Unit,
    onCreate: (UserManagementEvent.OnCreateUser) -> Unit,
    onUpdate: (UserManagementEvent.OnUpdateUser) -> Unit,
    onSendPasswordReset: () -> Unit,
    onDelete: () -> Unit,
    onRestore: () -> Unit,
)

@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun AddEditUserFormContent(
    editing: DatabaseUserModel?,
    isSaving: Boolean,
    isSendingPasswordReset: Boolean,
    passwordResetEmailSent: Boolean,
    currentUserId: String?,
    ownerOperators: List<OwnerOperator>,
    onDismiss: () -> Unit,
    onCreate: (UserManagementEvent.OnCreateUser) -> Unit,
    onUpdate: (UserManagementEvent.OnUpdateUser) -> Unit,
    onSendPasswordReset: () -> Unit,
    onDelete: () -> Unit,
    onRestore: () -> Unit,
) {
    val isNew = editing == null
    val isDeleted = editing?.deleted == true
    // You can't delete your own account.
    val isSelf = editing != null && editing.id == currentUserId

    var name by remember { mutableStateOf(editing?.name ?: "") }
    var payEntryId by remember { mutableStateOf(editing?.payEntryId ?: "") }
    var nickname by remember { mutableStateOf(editing?.nickname ?: "") }
    var email by remember { mutableStateOf(editing?.email ?: "") }
    var hireDate by remember { mutableStateOf(editing?.hireDate ?: Util.localDateTime().date) }
    var ptoBalanceText by remember { mutableStateOf(editing?.ptoBalance?.toString() ?: "0") }
    var fullTime by remember { mutableStateOf(editing?.fullTime ?: false) }
    var holidayTime by remember { mutableStateOf(editing?.holidayTime ?: false) }
    var role by remember { mutableStateOf(editing?.role ?: UserRole.DRIVER) }
    var ownerOperatorId by remember { mutableStateOf(editing?.ownerOperatorId) }

    var roleDropdownExpanded by remember { mutableStateOf(false) }
    var ownerOperatorDropdownExpanded by remember { mutableStateOf(false) }
    var showDatePicker by remember { mutableStateOf(false) }
    var showDeleteConfirm by remember { mutableStateOf(false) }

    val canSave = name.isNotBlank() &&
        email.isNotBlank() &&
        !isDeleted &&
        !isSaving

    NativeDialog(
        visible = showDeleteConfirm,
        title = "Deactivate Employee",
        message = "This deactivates the employee: their data is kept but they're hidden " +
            "and can no longer log in. You can reactivate them later.",
        confirmText = "Deactivate",
        onConfirm = onDelete,
        dismissText = "Cancel",
        onDismiss = { showDeleteConfirm = false }
    )

    NativeDatePicker(
        date = hireDate,
        showDatePicker = showDatePicker,
        onDismiss = { showDatePicker = false },
        onDateSelected = {
            hireDate = it
            showDatePicker = false
        }
    )

    Column(
        modifier = Modifier
            .responsiveDialogWidth()
            .fillMaxWidth()
            .verticalScroll(rememberScrollState())
            .padding(16.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(
            text = if (isNew) "Add Employee" else "Edit Employee",
            fontSize = 20.sp,
            fontWeight = FontWeight.Bold
        )
        Spacer(modifier = Modifier.height(16.dp))

        TimesheetTextField(text = name, label = "Name", onTextChange = { name = it })
        Spacer(modifier = Modifier.height(8.dp))

        TimesheetTextField(
            text = payEntryId,
            label = "PayEntry Employee ID",
            onTextChange = { payEntryId = it }
        )
        Spacer(modifier = Modifier.height(8.dp))

        TimesheetTextField(text = nickname, label = "Nickname", onTextChange = { nickname = it })
        Spacer(modifier = Modifier.height(8.dp))

        TimesheetTextField(
            text = email,
            label = "Email",
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
            onTextChange = { email = it }
        )
        Spacer(modifier = Modifier.height(8.dp))

        // Passwords are never set by admins: new users get a set-password link
        // in their welcome email; existing users can be sent a reset link.
        if (!isNew) {
            PasswordResetRow(
                isSending = isSendingPasswordReset,
                sent = passwordResetEmailSent,
                enabled = !isDeleted,
                onSend = onSendPasswordReset
            )
            Spacer(modifier = Modifier.height(8.dp))
        }

        // Hire date — a read-only field with a transparent clickable overlay
        // that opens the native date picker.
        Box {
            TimesheetTextField(
                text = Util.toFriendlyDate(hireDate),
                label = "Hire Date",
                readOnly = true
            )
            Box(
                modifier = Modifier
                    .matchParentSize()
                    .clickable { showDatePicker = true }
            )
        }
        Spacer(modifier = Modifier.height(8.dp))

        TimesheetTextField(
            text = ptoBalanceText,
            label = "PTO Balance (hours)",
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            onTextChange = { ptoBalanceText = it.filter { c -> c.isDigit() } }
        )
        Spacer(modifier = Modifier.height(8.dp))

        // Role picker (replaces the legacy Admin Access checkbox).
        ExposedDropdownMenuBox(
            expanded = roleDropdownExpanded,
            onExpandedChange = { roleDropdownExpanded = it }
        ) {
            TimesheetTextField(
                modifier = Modifier.menuAnchor(ExposedDropdownMenuAnchorType.PrimaryNotEditable),
                text = role.label(),
                label = "Role",
                readOnly = true,
                trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = roleDropdownExpanded) }
            )
            ExposedDropdownMenu(
                expanded = roleDropdownExpanded,
                onDismissRequest = { roleDropdownExpanded = false }
            ) {
                UserRole.entries.forEach { option ->
                    DropdownMenuItem(
                        text = { Text(option.label()) },
                        onClick = {
                            role = option
                            roleDropdownExpanded = false
                        }
                    )
                }
            }
        }
        Spacer(modifier = Modifier.height(8.dp))

        // Owner Operator picker — optional; "None" clears the association.
        ExposedDropdownMenuBox(
            expanded = ownerOperatorDropdownExpanded,
            onExpandedChange = { ownerOperatorDropdownExpanded = it }
        ) {
            val selectedName = ownerOperators
                .firstOrNull { it.id == ownerOperatorId }
                ?.contactName
                ?: "None"
            TimesheetTextField(
                modifier = Modifier.menuAnchor(ExposedDropdownMenuAnchorType.PrimaryNotEditable),
                text = selectedName,
                label = "Owner Operator",
                readOnly = true,
                trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = ownerOperatorDropdownExpanded) }
            )
            ExposedDropdownMenu(
                expanded = ownerOperatorDropdownExpanded,
                onDismissRequest = { ownerOperatorDropdownExpanded = false }
            ) {
                DropdownMenuItem(
                    text = { Text("None") },
                    onClick = {
                        ownerOperatorId = null
                        ownerOperatorDropdownExpanded = false
                    }
                )
                ownerOperators.forEach { option ->
                    DropdownMenuItem(
                        text = { Text(option.contactName.ifBlank { "(no name)" }) },
                        onClick = {
                            ownerOperatorId = option.id
                            ownerOperatorDropdownExpanded = false
                        }
                    )
                }
            }
        }
        Spacer(modifier = Modifier.height(8.dp))

        ToggleRow(label = "Full Time", checked = fullTime, onCheckedChange = { fullTime = it })
        ToggleRow(label = "Receives Holiday", checked = holidayTime, onCheckedChange = { holidayTime = it })

        Spacer(modifier = Modifier.height(8.dp))

        if (isSaving) {
            CircularProgressIndicator(color = MaterialTheme.colorScheme.tertiary)
            Spacer(modifier = Modifier.height(8.dp))
        }

        DialogButtonRow(
            confirmButtonText = "Save",
            confirmEnabled = canSave,
            onConfirmClick = {
                val pto = ptoBalanceText.toIntOrNull() ?: 0
                if (isNew) {
                    onCreate(
                        UserManagementEvent.OnCreateUser(
                            name = name.trim(),
                            email = email.trim(),
                            payEntryId = payEntryId.trim(),
                            nickname = nickname.trim(),
                            hireDate = hireDate,
                            ptoBalance = pto,
                            fullTime = fullTime,
                            holidayTime = holidayTime,
                            role = role,
                            ownerOperatorId = ownerOperatorId
                        )
                    )
                } else {
                    onUpdate(
                        UserManagementEvent.OnUpdateUser(
                            name = name.trim(),
                            email = email.trim(),
                            payEntryId = payEntryId.trim(),
                            nickname = nickname.trim(),
                            hireDate = hireDate,
                            ptoBalance = pto,
                            fullTime = fullTime,
                            holidayTime = holidayTime,
                            role = role,
                            ownerOperatorId = ownerOperatorId
                        )
                    )
                }
            },
            dismissButtonText = "Cancel",
            onDismissClick = onDismiss,
            neutralButtonText = when {
                isNew -> null
                isSelf -> null
                isDeleted -> "Reactivate"
                else -> "Deactivate"
            },
            onNeutralClick = if (isDeleted) onRestore else { { showDeleteConfirm = true } }
        )
    }
}

@Composable
private fun PasswordResetRow(
    isSending: Boolean,
    sent: Boolean,
    enabled: Boolean,
    onSend: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text("Password", fontSize = 16.sp)
        when {
            sent -> Text(
                text = "Reset email sent",
                fontSize = 14.sp,
                color = MaterialTheme.colorScheme.tertiary
            )
            isSending -> CircularProgressIndicator(
                modifier = Modifier.size(20.dp),
                strokeWidth = 2.dp,
                color = MaterialTheme.colorScheme.tertiary
            )
            else -> TextButton(onClick = onSend, enabled = enabled) {
                Text(
                    text = "Send Reset Email",
                    color = MaterialTheme.colorScheme.tertiary
                )
            }
        }
    }
}

@Composable
private fun ToggleRow(
    label: String,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(label, fontSize = 16.sp)
        Switch(
            checked = checked,
            onCheckedChange = onCheckedChange,
            colors = SwitchDefaults.colors(
                checkedThumbColor = MaterialTheme.colorScheme.tertiary,
                checkedTrackColor = MaterialTheme.colorScheme.tertiary.copy(alpha = 0.5f)
            )
        )
    }
}
